import Phaser from 'phaser';
import type { AthleteView, Pose } from './AthleteView';
import type { SkinDef } from './data/skins';
import { getSkin } from './data/skins';

const TAU = Math.PI * 2;

/** Baseline quadruped proportions in pixels, scaled per-skin by multipliers. */
const BASE = {
  bodyLen: 168,
  bodyHeight: 76,
  thigh: 48,
  shin: 46,
  legThick: 20,
  headR: 40,
  snout: 26,
  earR: 18,
  tailLen: 90,
  tailThick: 16,
} as const;

/**
 * A single two-segment leg built from nested containers so pivots are exact:
 * root rotates at the hip; knee rotates at the bottom of the thigh.
 */
class Leg {
  readonly root: Phaser.GameObjects.Container;
  private readonly knee: Phaser.GameObjects.Container;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    thighTex: string,
    shinTex: string,
    thighLen: number,
    depthBack: boolean,
  ) {
    const thigh = scene.add.sprite(0, 0, thighTex).setOrigin(0.5, 0);
    this.knee = scene.add.container(0, thighLen);
    const shin = scene.add.sprite(0, 0, shinTex).setOrigin(0.5, 0);
    this.knee.add(shin);
    this.root = scene.add.container(x, y, [thigh, this.knee]);
    // Far-side limbs sit behind the body and read slightly recessed.
    if (depthBack) this.root.setAlpha(0.82);
  }

  /** thighAngle and shinAngle are absolute-ish radians (shin is relative to thigh). */
  set(thighAngle: number, shinAngle: number): void {
    this.root.rotation = thighAngle;
    this.knee.rotation = shinAngle;
  }
}

export interface RigOptions {
  /** Facing direction: 1 = right, -1 = left. */
  facing?: number;
}

/**
 * Cutout rig that animates one athlete from a data-driven SkinDef. The same code drives
 * every species; only the SkinDef (colours + proportions) changes. Call `setMotion()`
 * each frame with a 0..1 speed value, then `update(dt)` to advance the gait.
 */
export class AthleteRig implements AthleteView {
  readonly container: Phaser.GameObjects.Container;
  private readonly skin: SkinDef;
  private readonly body: Phaser.GameObjects.Sprite;
  private readonly belly: Phaser.GameObjects.Sprite;
  private readonly head: Phaser.GameObjects.Container;
  private readonly ear: Phaser.GameObjects.Sprite;
  private readonly tail: Phaser.GameObjects.Container;
  private readonly legs: { backFar: Leg; backNear: Leg; frontFar: Leg; frontNear: Leg };
  private readonly bodyGroup: Phaser.GameObjects.Container;

  private phase = 0;
  private speedNorm = 0;
  private pose: Pose = 'idle';
  private readonly facing: number;
  private readonly bodyBaseY: number;

  constructor(scene: Phaser.Scene, x: number, y: number, skinId: string, opts: RigOptions = {}) {
    this.skin = getSkin(skinId);
    this.facing = opts.facing ?? 1;
    AthleteRig.buildTextures(scene, this.skin);

    const s = this.skin;
    const legLen = (BASE.thigh + BASE.shin) * s.legLen;
    const thighLen = BASE.thigh * s.legLen;
    const hipY = -legLen;
    const bodyLen = BASE.bodyLen * s.bodyLen;
    const bodyH = BASE.bodyHeight * s.bodyHeight;
    const tex = AthleteRig.texKeys(s.id);

    // Joints along the spine (athlete authored facing +x).
    const hipX = -bodyLen * 0.3;
    const shoulderX = bodyLen * 0.3;

    // Far legs first (behind body), then body, then near legs (in front).
    this.legs = {
      backFar: new Leg(scene, hipX, hipY, tex.thighFar, tex.shinFar, thighLen, true),
      frontFar: new Leg(scene, shoulderX, hipY * 0.98, tex.thighFar, tex.shinFar, thighLen, true),
      backNear: new Leg(scene, hipX, hipY, tex.thigh, tex.shin, thighLen, false),
      frontNear: new Leg(scene, shoulderX, hipY * 0.98, tex.thigh, tex.shin, thighLen, false),
    };

    // Body group (everything that bobs/leans together).
    this.body = scene.add.sprite(0, hipY - bodyH * 0.1, tex.body).setOrigin(0.5, 0.5);
    this.belly = scene.add.sprite(0, hipY - bodyH * 0.1 + bodyH * 0.18, tex.belly).setOrigin(0.5, 0.5);

    // Tail anchored at the rear hip, hangs/streams behind.
    const tailSprite = scene.add.sprite(0, 0, tex.tail).setOrigin(0.5, 0);
    this.tail = scene.add.container(hipX - bodyLen * 0.12, hipY - bodyH * 0.2, [tailSprite]);
    this.tail.rotation = Math.PI * 0.62;

    // Head with ear, snout, eye baked into a container.
    this.head = AthleteRig.buildHead(scene, s, tex);
    this.head.setPosition(shoulderX + bodyLen * 0.22, hipY - bodyH * 0.62);
    this.ear = this.head.list.find(
      (o): o is Phaser.GameObjects.Sprite => o instanceof Phaser.GameObjects.Sprite && o.texture.key === tex.ear,
    )!;

    this.bodyGroup = scene.add.container(0, 0, [this.tail, this.body, this.belly, this.head]);

    this.container = scene.add.container(x, y, [
      this.legs.backFar.root,
      this.legs.frontFar.root,
      this.bodyGroup,
      this.legs.backNear.root,
      this.legs.frontNear.root,
    ]);
    this.container.setScale(this.facing, 1);
    this.bodyBaseY = this.body.y;
    this.applyIdle(0);
  }

  /** Drive the run intensity (0 = still, 1 = full sprint). */
  setMotion(speedNorm: number): void {
    this.speedNorm = Phaser.Math.Clamp(speedNorm, 0, 1);
    if (this.pose === 'idle' && this.speedNorm > 0.02) this.pose = 'run';
  }

  setPose(pose: Pose): void {
    this.pose = pose;
  }

  /** Mirror the rig (e.g. facing the finish camera). */
  setFacing(facing: number): void {
    this.container.setScale(facing, 1);
  }

  update(dt: number): void {
    switch (this.pose) {
      case 'run':
        this.applyRun(dt);
        break;
      case 'celebrate':
        this.applyCelebrate(dt);
        break;
      case 'exhausted':
        this.applyExhausted(dt);
        break;
      default:
        this.applyIdle(dt);
    }
  }

  // --- pose implementations -------------------------------------------------

  private applyIdle(dt: number): void {
    this.phase += dt * 1.6;
    const breathe = Math.sin(this.phase) * 0.02;
    this.body.scaleY = 1 + breathe;
    this.body.scaleX = 1 - breathe * 0.5;
    this.body.y = this.bodyBaseY;
    this.bodyGroup.rotation = 0;
    const stand = 0.04 * Math.sin(this.phase);
    this.legs.backFar.set(stand, 0.05);
    this.legs.backNear.set(stand, 0.05);
    this.legs.frontFar.set(-stand, 0.05);
    this.legs.frontNear.set(-stand, 0.05);
    this.tail.rotation = Math.PI * 0.62 + Math.sin(this.phase * 0.8) * 0.08;
    this.head.y = -this.skin.headSize * 2 + Math.sin(this.phase) * 1.5 + this.headBaseY();
    this.ear.rotation = Math.sin(this.phase * 1.2) * 0.12;
  }

  private applyRun(dt: number): void {
    const sn = this.speedNorm;
    // Stride frequency rises with speed; bounce personality slows/quickens the legs.
    const freqHz = (2.4 + sn * 7.5) * this.skin.bounce;
    this.phase += dt * freqHz * TAU;
    const p = this.phase;

    const swing = Phaser.Math.Linear(0.28, 0.95, sn);
    const kneeAmp = Phaser.Math.Linear(0.5, 1.25, sn);

    // Diagonal (trot-like) gait reads clean for a cartoon runner.
    this.driveLeg(this.legs.backNear, p, swing, kneeAmp);
    this.driveLeg(this.legs.frontFar, p, swing, kneeAmp);
    this.driveLeg(this.legs.backFar, p + Math.PI, swing, kneeAmp);
    this.driveLeg(this.legs.frontNear, p + Math.PI, swing, kneeAmp);

    // Body bobs twice per stride; squash & stretch on the bounce.
    const bob = Phaser.Math.Linear(2, 17, sn) * this.skin.bounce;
    const bounce = Math.sin(p * 2);
    this.body.y = this.bodyBaseY - (0.5 + 0.5 * bounce) * bob;
    this.body.scaleX = 1 + bounce * 0.05 * sn;
    this.body.scaleY = 1 - bounce * 0.05 * sn;
    this.belly.y = this.body.y + BASE.bodyHeight * this.skin.bodyHeight * 0.18;
    this.belly.scaleX = this.body.scaleX;
    this.belly.scaleY = this.body.scaleY;

    // Forward lean grows with speed.
    this.bodyGroup.rotation = Phaser.Math.Linear(0, -0.16, sn);

    // Tail streams behind, lagging the body for life.
    this.tail.rotation = Math.PI * 0.62 + Math.sin(p - 0.7) * (0.22 + sn * 0.3);

    // Head bobs counter to the body; ears flop with delay (secondary motion).
    this.head.y = this.headBaseY() - (0.5 - 0.5 * bounce) * bob * 0.5;
    this.head.rotation = Phaser.Math.Linear(0, -0.1, sn) + Math.sin(p * 2) * 0.04;
    this.ear.rotation = Math.sin(p - 1.1) * (0.2 + sn * 0.5);
  }

  private driveLeg(leg: Leg, legPhase: number, swing: number, kneeAmp: number): void {
    const thigh = Math.sin(legPhase) * swing;
    // Knee tucks during the forward (recovery) swing only.
    const tuck = Math.max(0, Math.sin(legPhase + 0.6));
    leg.set(thigh, tuck * kneeAmp);
  }

  private applyCelebrate(dt: number): void {
    this.phase += dt * 6;
    const j = Math.abs(Math.sin(this.phase));
    this.body.y = this.bodyBaseY - j * 22;
    this.bodyGroup.rotation = -0.05;
    this.legs.frontFar.set(-1.1, 0.2);
    this.legs.frontNear.set(-1.2, 0.2);
    this.legs.backFar.set(0.3 + j * 0.2, 0.6);
    this.legs.backNear.set(0.3 + j * 0.2, 0.6);
    this.tail.rotation = Math.PI * 0.62 + Math.sin(this.phase * 2) * 0.5;
    this.ear.rotation = Math.sin(this.phase * 2) * 0.4;
    this.head.rotation = -0.15;
  }

  private applyExhausted(dt: number): void {
    this.phase += dt * 2.2;
    const pant = Math.sin(this.phase) * 0.04;
    this.body.y = this.bodyBaseY + 6;
    this.body.scaleY = 1 + pant;
    this.bodyGroup.rotation = 0.08;
    this.legs.frontFar.set(0.4, 0.1);
    this.legs.frontNear.set(0.5, 0.1);
    this.legs.backFar.set(-0.3, 0.1);
    this.legs.backNear.set(-0.35, 0.1);
    this.head.rotation = 0.2 + pant;
    this.ear.rotation = 0.3;
    this.tail.rotation = Math.PI * 0.7;
  }

  private headBaseY(): number {
    const s = this.skin;
    const legLen = (BASE.thigh + BASE.shin) * s.legLen;
    const bodyH = BASE.bodyHeight * s.bodyHeight;
    return -legLen - bodyH * 0.62;
  }

  destroy(): void {
    this.container.destroy();
  }

  // --- texture generation (cached per skin) ---------------------------------

  private static texKeys(skinId: string) {
    const k = `rig:${skinId}:`;
    return {
      body: `${k}body`,
      belly: `${k}belly`,
      thigh: `${k}thigh`,
      shin: `${k}shin`,
      thighFar: `${k}thighFar`,
      shinFar: `${k}shinFar`,
      tail: `${k}tail`,
      head: `${k}head`,
      ear: `${k}ear`,
      snout: `${k}snout`,
      eye: `${k}eye`,
    };
  }

  private static buildTextures(scene: Phaser.Scene, s: SkinDef): void {
    const tex = AthleteRig.texKeys(s.id);
    const bodyLen = BASE.bodyLen * s.bodyLen;
    const bodyH = BASE.bodyHeight * s.bodyHeight;
    const thighLen = BASE.thigh * s.legLen;
    const shinLen = BASE.shin * s.legLen;
    const legW = BASE.legThick * s.legThick;
    const headR = BASE.headR * s.headSize;
    const earR = BASE.earR * s.headSize;
    const tailLen = BASE.tailLen * (s.tail === 'long' ? 1 : s.tail === 'short' ? 0.55 : 0.4);

    const darker = (c: number, f: number) => Phaser.Display.Color.IntegerToColor(c).darken(f * 100).color;

    AthleteRig.tex(scene, tex.body, bodyLen + 8, bodyH + 8, (g) => {
      g.fillStyle(s.body, 1);
      g.fillEllipse((bodyLen + 8) / 2, (bodyH + 8) / 2, bodyLen, bodyH);
    });
    AthleteRig.tex(scene, tex.belly, bodyLen * 0.7, bodyH * 0.55, (g) => {
      g.fillStyle(s.belly, 1);
      g.fillEllipse((bodyLen * 0.7) / 2, (bodyH * 0.55) / 2, bodyLen * 0.66, bodyH * 0.5);
    });

    const capsule = (key: string, len: number, color: number) =>
      AthleteRig.tex(scene, key, legW, len, (g) => {
        g.fillStyle(color, 1);
        g.fillRoundedRect(0, 0, legW, len, legW / 2);
      });
    capsule(tex.thigh, thighLen + legW / 2, s.limb);
    capsule(tex.shin, shinLen + legW / 2, darker(s.limb, 0.06));
    capsule(tex.thighFar, thighLen + legW / 2, darker(s.limb, 0.16));
    capsule(tex.shinFar, shinLen + legW / 2, darker(s.limb, 0.22));

    AthleteRig.tex(scene, tex.tail, BASE.tailThick, tailLen, (g) => {
      g.fillStyle(s.limb, 1);
      g.fillRoundedRect(0, 0, BASE.tailThick, tailLen, BASE.tailThick / 2);
      if (s.tail === 'long') {
        g.fillStyle(s.accent, 1);
        g.fillCircle(BASE.tailThick / 2, tailLen - BASE.tailThick / 2, BASE.tailThick * 0.7);
      }
    });

    const headD = headR * 2 + 8;
    AthleteRig.tex(scene, tex.head, headD, headD, (g) => {
      g.fillStyle(s.body, 1);
      g.fillCircle(headD / 2, headD / 2, headR);
    });
    AthleteRig.tex(scene, tex.snout, headR * 1.1, headR * 0.8, (g) => {
      g.fillStyle(s.belly, 1);
      g.fillEllipse((headR * 1.1) / 2, (headR * 0.8) / 2, headR, headR * 0.7);
      g.fillStyle(s.accent, 1);
      g.fillEllipse((headR * 1.1) / 2, (headR * 0.8) * 0.35, headR * 0.35, headR * 0.25);
    });
    const earD = earR * 2 + 4;
    AthleteRig.tex(scene, tex.ear, earD, earD, (g) => {
      g.fillStyle(s.body, 1);
      if (s.earStyle === 'pointy') {
        g.fillTriangle(0, earD, earD / 2, 0, earD, earD);
      } else {
        g.fillCircle(earD / 2, earD / 2, earR);
      }
      g.fillStyle(s.accent, 0.6);
      g.fillCircle(earD / 2, earD * 0.55, earR * 0.5);
    });
    const eyeD = Math.max(10, headR * 0.6 * s.eye);
    AthleteRig.tex(scene, tex.eye, eyeD, eyeD, (g) => {
      g.fillStyle(0xffffff, 1);
      g.fillCircle(eyeD / 2, eyeD / 2, eyeD / 2);
      g.fillStyle(0x1a1a1a, 1);
      g.fillCircle(eyeD * 0.58, eyeD / 2, eyeD * 0.26);
      g.fillStyle(0xffffff, 1);
      g.fillCircle(eyeD * 0.66, eyeD * 0.4, eyeD * 0.09);
    });
  }

  private static buildHead(scene: Phaser.Scene, s: SkinDef, tex: ReturnType<typeof AthleteRig.texKeys>) {
    const headR = BASE.headR * s.headSize;
    const ear = scene.add.sprite(-headR * 0.2, -headR * 0.85, tex.ear).setOrigin(0.5, 1);
    const head = scene.add.sprite(0, 0, tex.head).setOrigin(0.5, 0.5);
    const snout = scene.add.sprite(headR * 0.7, headR * 0.2, tex.snout).setOrigin(0.5, 0.5);
    const eye = scene.add.sprite(headR * 0.35, -headR * 0.1, tex.eye).setOrigin(0.5, 0.5);
    return scene.add.container(0, 0, [ear, head, snout, eye]);
  }

  private static tex(
    scene: Phaser.Scene,
    key: string,
    w: number,
    h: number,
    draw: (g: Phaser.GameObjects.Graphics) => void,
  ): void {
    if (scene.textures.exists(key)) return;
    const g = scene.make.graphics({ x: 0, y: 0 });
    draw(g);
    g.generateTexture(key, Math.ceil(w), Math.ceil(h));
    g.destroy();
  }
}
